import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { makeTestId } from '../../utils/Declarative';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { clamp } from '../../utils/Math';
import { TabPanel } from './TabPanel';
import * as Styles from './TabsWidgetStyles';
import { TabProps, TabWidget } from './TabWidget';

/**
 * Tabs widget component that renders a tab list and tab panel(s). It renders the active tab's content, in addition to the
 * content of the anchor tab (user-specified), which is rendered to maintain a consistent height when switching tabs.
 *
 * Nomenclature (inspired by https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel):
 *   - tabs widget: the name of the entire widget
 *   - tab list: a set of tab widgets
 *   - tab: an item in the tab list that serves as a label for one of the tab panels; when active, its panel is shown
 *   - tab indicator: the solid line at the bottom of each tab that changes color if the tab is active, hovered, or pressed
 *   - tab panel: the component that contains the content associated with a tab
 */

/**
 * Mode that dictates whether tabs are activated automatically or manually.
 *   Automatic: tabs are automatically activated and their panel is displayed when they receive focus
 *   Manual: users activate a tab and display its panel by pressing space or enter
 * More information: https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
 */
export enum TabActivationMode {
  Automatic,
  Manual,
}

/**
 * Dictates the alignment of the tabs within the tab container.
 *   Justified: evenly distributes the tabs within the available container width
 *   Left: left aligns the tabs within the container
 */
export enum TabListAlignment {
  Left,
  Justified,
}

export interface TabContent {
  icon?: React.ReactNode;
  label: string;

  /**
   * If no title is provided, we'll default to using the label for the title
   */
  title?: string;
}

export interface TabAndPanelContents {
  panelContent: React.ReactNode;
  tabContent: TabContent;
}

export interface TabsProps {
  /**
   * Optional parameter. Dictates whether tabs are activated automatically or manually.
   *   Automatic: tabs are automatically activated and their panel is displayed when they receive focus
   *   Manual: users activate a tab and display its panel by pressing space or enter
   * More information: https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
   *
   * @default ```TabActivationMode.Automatic```
   */
  activationMode?: TabActivationMode;

  /**
   * When provided, the entire TabsWidget will fix its height based on the panel content for the corresponding tab.
   * Otherwise, the tab panel content will freely size to fit the current tab's content.
   */
  anchorTabIndex?: number;

  /** `aria-label` attribute for the Tabs list */
  ariaLabel: string;

  /**
   * Avoid using this. If you think you need it, discuss first with your designer and the Widgets team.
   */
  dimensionOverrides?: { tabHeight?: number, tabHorizontalPadding?: number, tabPanelPadding?: React.CSSProperties['padding'] };

  /**
   * Used to set the initial active tab index.
   */
  initialActiveIndex: number;

  /**
   * The function that will be called when the active index changes.
   * NOTE: This function is called after the TabsWidget has been re-rendered with the updated active index.
   */
  onActiveIndexChanged?: (index: number) => void;

  /**
   * Indicates what tab alignment should be used within the tab container.
   *   TabListAlignment.Justified - will evenly distribute the tabs within the available container width
   *   TabListAlignment.Left - will evenly distribute the tabs within the available container width
   * Default is TabListAlignment.Justified
   */
  tabListAlignment?: TabListAlignment;

  /**
   * An array of the tab and panel contents to be displayed.
   * NOTE: The design of this component is for 2+ tabs.
   */
  tabs: TabAndPanelContents[];

  testId: string;
}

export interface TabsState {
  activeIndex: number;
  focusedIndex: number; // this should only be updated by focus, blur, click, and keyboard events in the tabs widget
}

export class TabsWidget extends React.PureComponent<TabsProps & ElemAttrs, TabsState> {
  public static defaultProps: Pick<TabsProps, 'activationMode'> = {
    activationMode: TabActivationMode.Automatic,
  };

  private tablistRef: React.RefObject<HTMLDivElement> = React.createRef();
  public state: TabsState = {
    activeIndex: clamp(this.props.initialActiveIndex, 0, this.props.tabs.length - 1),
    focusedIndex: -1,
  };

  public componentDidUpdate(prevProps: TabsProps, prevState: TabsState): void {
    const { activeIndex, focusedIndex } = this.state;
    if (focusedIndex > -1 && focusedIndex !== prevState.focusedIndex) {
      this.tablistRef.current?.querySelectorAll<HTMLElement>(`[role="tab"]`).item(focusedIndex)?.focus();
    }
    if (activeIndex !== prevState.activeIndex) {
      this.props.onActiveIndexChanged?.(activeIndex);
    }
  }

  public setActiveIndex = (index: number): void => {
    // Any active index should also gain focus
    this.setState({ activeIndex: index, focusedIndex: index });
  }

  public setFocusedIndex = (index: number): void => {
    this.setState({ focusedIndex: index });
  }

  private buildTabId(index: number): string {
    return makeTestId(this.props.testId, `tab${index + 1}`);
  }

  private buildTabPanelId(index: number): string {
    return makeTestId(this.props.testId, `tabpanel${index + 1}`);
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = e => {
    const tabIndex = Array.from(e.currentTarget.querySelectorAll(`[role="tab"]`)).indexOf(e.target as HTMLElement);
    const numTabs = this.props.tabs.length;

    const setIndex = this.props.activationMode === TabActivationMode.Manual ? this.setFocusedIndex : this.setActiveIndex;

    const keyHandlers = {
      'ArrowRight': () => setIndex((tabIndex + 1) % numTabs),
      'ArrowLeft': () => setIndex((numTabs + tabIndex - 1) % numTabs),
      'Home': () => setIndex(0),
      'End': () => setIndex(numTabs - 1),
      'Enter': () => this.setActiveIndex(tabIndex),
      ' ': () => this.setActiveIndex(tabIndex),
    };

    const handler = keyHandlers[e.key as keyof typeof keyHandlers];
    if (handler) {
      e.preventDefault();
      e.stopPropagation();
      handler();
    }
  }

  private renderTabList(): React.ReactChild[] {
    const indexOfTabbableTab = this.state.focusedIndex > -1 ? this.state.focusedIndex : this.state.activeIndex;
    return this.props.tabs.map((tab: TabAndPanelContents, index: number) => {
      const tabProps: TabProps = {
        ariaControls: this.buildTabPanelId(index),
        id: this.buildTabId(index),
        index: index,
        isActive: this.isTabActive(index),
        onBlur: () => this.setFocusedIndex(-1),
        onClick: this.setActiveIndex,
        onFocus: this.setFocusedIndex,
        tabContainerStyles: Styles.GetTabLabelContainerStyles(this.props.dimensionOverrides?.tabHorizontalPadding),
        tabContent: tab.tabContent,
        tabIndex: index === indexOfTabbableTab ? 0 : -1,
      };

      if (this.props.tabListAlignment === undefined || this.props.tabListAlignment === TabListAlignment.Justified) {
        tabProps.flexPercent = 100 / this.props.tabs.length;
      }

      return <TabWidget key={index} {...tabProps} />;
    });
  }

  private isTabActive(tabIndex: number): boolean {
    return tabIndex === this.state.activeIndex;
  }

  private isAnchorTabActive(): boolean {
    return this.isTabActive(this.props.anchorTabIndex!);
  }

  private renderTabPanel(index: number): React.ReactChild {
    return <TabPanel
      ariaLabelledBy={this.buildTabId(index)}
      containerStyle={this.getTabPanelContainerStyle(index)}
      content={this.props.tabs[index].panelContent}
      key={index}
      id={this.buildTabPanelId(index)}
    />;
  }

  private getTabPanelContainerStyle(index: number): React.CSSProperties {
    const { anchorTabIndex } = this.props;
    if (typeof anchorTabIndex !== 'number') {
      return {};
    } else if (index === anchorTabIndex) {
      return this.isAnchorTabActive() ? Styles.Visible : Styles.Hidden;
    }
    return Styles.NonAnchorTabPanelStyles;
  }

  private isAnIconDefinedInTabs(): boolean {
    for (let idx = 0; idx < this.props.tabs.length; idx++) {
      if (!!this.props.tabs[idx].tabContent.icon) {
        return true;
      }
    }

    return false;
  }

  public render(): JSX.Element {
    // tslint:disable-next-line:max-line-length
    const { activationMode, anchorTabIndex, ariaLabel, dimensionOverrides, initialActiveIndex, onActiveIndexChanged, tabListAlignment, tabs, testId, ...htmlAttrs } = this.props;

    return (
      <div {...htmlAttrs} className={TypeStyle.classes(Styles.RootStyleName, htmlAttrs.className)}>
        <div
          aria-label={ariaLabel}
          children={this.renderTabList()}
          data-tb-test-id={makeTestId(testId, 'tablist')}
          onKeyDown={this.handleKeyDown}
          ref={this.tablistRef}
          role='tablist'
          style={Styles.GetTabListStyles(this.isAnIconDefinedInTabs(), dimensionOverrides?.tabHeight)}
        />
        <div
          data-tb-test-id={makeTestId(testId, 'tabpanel-scroll-container')}
          style={Styles.TabPanelScrollContainerStyles(dimensionOverrides?.tabPanelPadding)}
        >
          <div
            data-tb-test-id={makeTestId(testId, 'tabpanel-container')}
            style={Styles.TabPanelContainerStyles}
          >
            {typeof anchorTabIndex === 'number' && !this.isAnchorTabActive() && this.renderTabPanel(anchorTabIndex!)}
            {this.renderTabPanel(this.state.activeIndex)}
          </div>
        </div>
      </div>
    );
  }
}
